import React, { Component, type SyntheticEvent } from 'react';
import type { UIAnalyticsEvent, CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import authenticateUsingAppLinks from '@atlassian/jira-common-authenticate-using-app-links/src/index.tsx';
import OpTrigger from '@atlassian/jira-common-util-op-triggers/src/on-mount/OpTrigger.tsx';
import PageSpinner from '@atlassian/jira-servicedesk-common/src/ui/components/page-spinner/index.tsx';
import { UserCapabilitiesSubscriber } from '@atlassian/jira-servicedesk-knowledge-common/src/controllers/store/index.tsx';
import type {
	Article,
	GenericError,
	ArticleType,
} from '@atlassian/jira-servicedesk-knowledge-services/src/types.tsx';
import type { AccountId } from '@atlassian/jira-shared-types/src/general.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { GlobalPageLoadExperience } from '@atlassian/ufo';
import type { AppLinkState } from '../../state/entities/applink/types.tsx';
import AuthenticationPanelWithBreadcrumbs from '../common/applink-authentication/authentication-panel-with-breadcrumbs/index.tsx';
import AppLinkErrorPanel from '../common/applink-error-panel/index.tsx';
import ErrorPage from '../common/error-page/index.tsx';
import KnowledgeHubArticleStateless from '../knowledge-hub-article-stateless/index.tsx';

type KnowledgeHubArticleProps = {
	id: string;
	activationId: string;
	accountId: AccountId | null;
	article: Article | GenericError | undefined;
	appLinkState: AppLinkState | undefined;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	projectKey: string | undefined;
	projectId: number;
	spaceKey: string | undefined;
	onFetchAppLink: () => void;
	onFetchArticle: (
		id: string,
		articleType: ArticleType,
		accountId: AccountId | null,
		createAnalyticsEvent: CreateUIAnalyticsEvent,
	) => void;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onEditClicked: ((e: SyntheticEvent<any>, analyticsEvent: UIAnalyticsEvent) => void) | undefined;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onCopyClicked: ((e: SyntheticEvent<any>, analyticsEvent: UIAnalyticsEvent) => void) | undefined;
};
type KnowledgeHubArticleState = {
	iframeLoading: boolean;
};

// eslint-disable-next-line jira/react/no-class-components
export default class KnowledgeHubArticle extends Component<
	KnowledgeHubArticleProps,
	KnowledgeHubArticleState
> {
	static defaultProps = {
		projectKey: undefined,
		onEditClicked: undefined,
		onCopyClicked: undefined,
	};

	state = {
		iframeLoading: true,
	};

	componentDidMount() {
		const { id, appLinkState, onFetchArticle, onFetchAppLink, accountId, createAnalyticsEvent } =
			this.props;
		onFetchArticle(id, 'article', accountId, createAnalyticsEvent);
		// Fetch applink only if it has not been fetched before
		if (!appLinkState?.isLoaded) {
			onFetchAppLink();
		}
	}

	componentDidUpdate(prevProps: KnowledgeHubArticleProps) {
		const { id, onFetchArticle, accountId, createAnalyticsEvent } = this.props;
		if (prevProps.id !== id) {
			onFetchArticle(id, 'article', accountId, createAnalyticsEvent);
		}
	}

	onIFrameLoaded = () => {
		this.setState({ iframeLoading: false });
	};

	render() {
		const {
			article,
			appLinkState,
			onFetchArticle: _,
			onFetchAppLink: __,
			id,
			projectId,
			activationId,
			createAnalyticsEvent,
			accountId,
			...passThroughProps
		} = this.props;

		const showAppLinkLoading = !appLinkState?.isLoaded;
		const showSpinner = showAppLinkLoading;
		if (showSpinner) {
			return <PageSpinner />;
		}

		if (appLinkState?.error) {
			GlobalPageLoadExperience.markFMP();
			GlobalPageLoadExperience.failure({ force: true });
			return <AppLinkErrorPanel />;
		}

		if (appLinkState?.appLink?.requireCredentials) {
			const onAuthenticate = async () => {
				try {
					await authenticateUsingAppLinks(appLinkState?.appLink?.authUrl || '');
					this.props.onFetchAppLink();
					this.props.onFetchArticle(this.props.id, 'article', accountId, createAnalyticsEvent);
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
				} catch (error: any) {
					// do nothing
				}
			};
			return (
				<AuthenticationPanelWithBreadcrumbs onAuthenticate={onAuthenticate} showDefaultTitle />
			);
		}

		// @ts-expect-error - TS2339 - Property 'error' does not exist on type 'Article | GenericError'.
		if (article && article.error) {
			GlobalPageLoadExperience.markFMP();
			GlobalPageLoadExperience.failure({ force: true });
			return (
				<ErrorPage
					// @ts-expect-error - TS2339 - Property 'error' does not exist on type 'Article | GenericError'.
					error={article.error}
					screenName="knowledgeArticleError"
					attributes={{
						isConfluenceServer: appLinkState?.appLink?.isServer ?? false,
					}}
				/>
			);
		}

		return (
			<UFOSegment name="knowledge-hub-view-article">
				<UserCapabilitiesSubscriber>
					{({ data }, { fetchUserCapabilities }) => {
						const { hasConfluenceLicense } = data;
						return (
							<>
								<OpTrigger
									op={fetchUserCapabilities}
									args={[projectId, activationId, createAnalyticsEvent({})]}
								/>
								<KnowledgeHubArticleStateless
									{...passThroughProps}
									isServer={appLinkState?.appLink?.isServer}
									article={article}
									onIFrameLoaded={this.onIFrameLoaded}
									iframeLoading={this.state.iframeLoading}
									canViewSpaceUrl={hasConfluenceLicense}
									articleId={this.props.id}
								/>
							</>
						);
					}}
				</UserCapabilitiesSubscriber>
			</UFOSegment>
		);
	}
}
